import React, { useState, useEffect, useRef } from 'react';
import { TOKENS } from '../tokens';
import api from '../api/client';
import { Truck, CheckCircle2, Camera, MapPin, FileUp, Upload } from 'lucide-react';
import { captureLocation } from '../hooks/useGPS';

export function EmployeeDeliveryScreen({ emp }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStep, setSelectedStep] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDeliveries();
  }, []);

  const fetchDeliveries = async () => {
    try {
      const { data } = await api.get('/deliveries');
      // For each delivery, fetch details to get steps assigned to this employee
      const detailed = await Promise.all(
        data.map(async d => {
          const res = await api.get(`/deliveries/${d.delivery_id}`);
          // filter steps for this employee
          res.data.steps = res.data.steps.filter(s => s.assignee_id === emp.id);
          return res.data;
        })
      );
      // Filter out deliveries with no steps assigned to this employee
      setDeliveries(detailed.filter(d => d.steps.length > 0));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = (step) => {
    setSelectedStep(step);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedStep) return;

    setUploading(true);
    
    // Capture GPS
    captureLocation(async (gps) => {
      try {
        const formData = new FormData();
        formData.append('proofFile', file);
        if (gps && gps.lat) {
          formData.append('lat', gps.lat);
          formData.append('lng', gps.lng);
        }

        await api.post(`/deliveries/steps/${selectedStep.step_id}/proofs`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        alert("Proof uploaded successfully!");
        fetchDeliveries(); // Refresh
      } catch (err) {
        alert("Upload failed: " + (err.response?.data?.error || err.message));
      } finally {
        setUploading(false);
        setSelectedStep(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    });
  };

  return (
    <div style={{ padding: '16px 20px', paddingBottom: 100 }}>
      <h2 style={{ margin: '0 0 20px 0', fontFamily: 'Fraunces', color: TOKENS.navyDeep, fontSize: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Truck color={TOKENS.gold} /> My Deliveries
      </h2>

      {/* Hidden file input */}
      <input 
        type="file" 
        accept="image/*,application/pdf"
        ref={fileInputRef} 
        onChange={handleFileChange} 
        style={{ display: 'none' }} 
        disabled={uploading}
      />

      {loading ? (
        <p>Loading assignments...</p>
      ) : deliveries.length === 0 ? (
        <div style={{ background: '#fff', padding: 32, borderRadius: 16, textAlign: 'center', color: '#666', border: '1px solid #eee' }}>
          <CheckCircle2 size={40} style={{ opacity: 0.2, marginBottom: 16 }} />
          <h3 style={{ margin: '0 0 8px 0' }}>No active deliveries</h3>
          <p style={{ margin: 0, fontSize: 14 }}>You have no site checklist steps assigned right now.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {deliveries.map(d => (
            <div key={d.delivery_id} style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #eee' }}>
              <div style={{ background: TOKENS.navyDeep, color: '#fff', padding: 16 }}>
                <div style={{ fontSize: 12, color: TOKENS.goldLight, fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>{d.project_name.toUpperCase()}</div>
                <h3 style={{ margin: 0, fontSize: 18 }}>{d.site_name}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#C7D2E3', marginTop: 8 }}>
                  <MapPin size={12} /> {d.address}
                </div>
              </div>
              
              <div style={{ padding: 16 }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#666' }}>My Checklist Steps</h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {d.steps.map(step => (
                    <div key={step.step_id} style={{ 
                      border: '1px solid #eee', borderRadius: 12, padding: 12,
                      background: step.done_at ? '#F8FCF9' : '#fff',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, color: step.done_at ? '#1E8E3E' : TOKENS.navyDeep, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {step.done_at && <CheckCircle2 size={16} />} {step.title}
                        </div>
                        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                          Requires: {step.proof_type === 'photo' ? 'Photo with GPS' : 'Document/Report'}
                        </div>
                      </div>
                      
                      {!step.done_at && (
                        <button 
                          onClick={() => handleUploadClick(step)}
                          disabled={uploading}
                          style={{
                            background: TOKENS.gold, color: TOKENS.navyDeep,
                            border: 'none', padding: '8px 12px', borderRadius: 8,
                            fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                            opacity: uploading ? 0.7 : 1, cursor: uploading ? 'default' : 'pointer'
                          }}
                        >
                          {step.proof_type === 'photo' ? <Camera size={14} /> : <FileUp size={14} />} 
                          {uploading && selectedStep?.step_id === step.step_id ? 'Uploading...' : 'Upload Proof'}
                        </button>
                      )}
                      
                      {step.done_at && (
                        <div style={{ fontSize: 12, color: '#1E8E3E', fontWeight: 600, background: '#E6F4EA', padding: '4px 10px', borderRadius: 12 }}>
                          Completed
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
